import { getCsrfToken } from "@/lib/workspace-api"
import { VoiceTranscribeError } from "@/lib/voice-dictation"

export type AgentContextType =
  | "project"
  | "shot"
  | "character"
  | "location"
  | "prop"
  | "canvas"
  | "flow"
  | "flow_node"
  | "company"
  | "scene3d"

export type Scene3dAgentFocus = {
  scene3d_id: number
  dirty?: boolean
  opened_from?: "gallery" | "shot"
  shot_id?: number | null
  selection?: {
    object_uid?: string | null
    sectors?: { x: number; z: number }[]
    face_tiles?: { face_key: string; u: number; v: number }[]
  }
  scene_state?: object
}

export type Scene3dLayoutUpdated = {
  scene3d_id: number
  layout: Record<string, unknown>
}

/** Project workspace agent vs company canvas/skills (`/company/agent`) vs studio skills (`/studio/agent`). */
export type AgentApiScope =
  | { kind: "project"; projectId: number }
  | { kind: "company"; companyId?: number }
  | { kind: "studio" }

function agentScopeCompanyId(scope: AgentApiScope | undefined): number | undefined {
  if (scope?.kind !== "company") return undefined
  const id = scope.companyId
  return id != null && id > 0 ? id : undefined
}

function agentCompanyBody(scope: AgentApiScope | undefined): { company_id?: number } {
  const id = agentScopeCompanyId(scope)
  return id == null ? {} : { company_id: id }
}

export function agentApiBase(scope: AgentApiScope): string {
  if (scope.kind === "company") return "/company/agent"
  if (scope.kind === "studio") return "/studio/agent"
  return `/projects/${scope.projectId}/workspace/agent`
}

export function resolveAgentApiScope(scopeOrProjectId: AgentApiScope | number): AgentApiScope {
  return typeof scopeOrProjectId === "number" ? { kind: "project", projectId: scopeOrProjectId } : scopeOrProjectId
}

function agentScopeFromParams(params: { projectId?: number; agentScope?: AgentApiScope }): AgentApiScope {
  if (params.agentScope) return params.agentScope
  if (params.projectId != null) return { kind: "project", projectId: params.projectId }
  throw new Error("agentScope or projectId required")
}

export type AgentPendingAction = {
  tool_name: string
  arguments: Record<string, unknown>
  human_readable_summary: string
  tool_call_id?: string
}

export type AgentMessageImage = {
  id: number
  filename: string
  content_type?: string | null
  thumb_url?: string | null
  download_url?: string | null
  /** Redirect на хранилище. Плеер берёт его, а не proxy download_url. */
  stream_url?: string | null
  /** Локальный blob: или готовый thumb. Только эта сессия, сервер поле не присылает. */
  preview_url?: string | null
}

export type AgentMessageAuthor = {
  id?: number | string
  email?: string
  first_name?: string | null
  last_name?: string | null
}

export type AgentMessageRow = {
  id: number
  role: string
  content: string | null
  tool_call_id?: string | null
  pending_action?: AgentPendingAction | null
  meta?: Record<string, unknown>
  author?: AgentMessageAuthor | null
  images?: AgentMessageImage[]
  created_at?: string
  /** Stable React key across optimistic → server id remap. */
  clientKey?: string
}

export type AgentShotPromptUpdated = {
  shot_id: number
  target: string
  improved_prompt: string
  shot_prompt_version_id?: number
  camera_preset_ids?: number[]
  panel_count?: number
}

export type AgentSavedPromptCard = {
  shot_id?: number
  node_id?: number
  label?: string | null
  target: "image" | "video" | "prompt" | string
  text: string
}

export type AgentFlowNodePromptUpdated = {
  node_id: number
  prompt_text: string
  prompt_doc?: Record<string, unknown> | null
}

export type AgentShotDurationUpdated = {
  shot_id: number
  duration_target_sec: number
}

export type AgentShotVideoReferenceRow = {
  shot_id: number
  send_animatic: boolean
  animatic_mode: string
  send_shooting_board: boolean
}

export type AgentShotVideoReferencesUpdated = {
  shot_ids: number[]
  updated?: AgentShotVideoReferenceRow[]
  skipped?: { shot_id: number; reason: string }[]
}

export type AgentShotCreated = {
  shot_id: number
  number: string
  shot_kind: string
}

export type AgentSceneCreated = {
  scene_id: number
  number: number
  title?: string | null
  episode_id: number
}

export type AgentHandoffSurfaceName = "characters" | "locations" | "props" | "shot" | "tasks"

/** meta делегирующей плашки в сообщении ассистента (meta.kind === "handoff"). */
export type AgentHandoffChipMeta = {
  kind: "handoff"
  status: "running" | "done" | "failed"
  surface: AgentHandoffSurfaceName
  task?: string
  target_conversation_id: number
  target_mode?: string
  target_context_type?: string
  target_context_id?: number
  entity_id?: number
  shot_id?: number
  finished_at?: string
}

/** Компактный статус прогона ассистента из общего стрима проекта. */
export type AgentProjectRun = {
  conversation_id: number
  mode: string
  context_type: string
  context_id: number
  status: AgentRunState["status"]
  phase?: string | null
  started_at?: string | null
}

export type AgentShotsBatchQueued = {
  generation_job_id: number
  shots_count: number
}

export type AgentEntityUpdated = {
  entity_type: string
  id: number | null
  name: string
  profile_text?: string | null
  missing_profile_sections?: string[]
  warning?: string | null
  deleted?: boolean
}

export type AgentCanvasDraftUpdated = {
  prompt_text?: string
  prompt_text_by_kind?: { image?: string; video?: string }
  changes_summary?: string
  rationale?: string
  media_kind?: "image" | "video"
  model_id?: string
  aspect_ratio?: string
  duration_sec?: number
}

export type AgentShotCharactersUpdated = {
  shot_id: number
  character_ids: number[]
  storyboard_image_prompt?: string | null
  video_kling_prompt?: string | null
  stripped_prompt_targets?: string[]
}

export type AgentShotLocationUpdated = {
  shot_id: number
  location_id: number | null
  location_variant_id?: number | null
  storyboard_image_prompt?: string | null
  video_kling_prompt?: string | null
  stripped_prompt_targets?: string[]
}

export type AgentShotSceneUpdated = {
  shot_id: number
  scene_id: number
  number: string
  scene_number: number
  label: string
  previous_scene_id?: number
  previous_label?: string
}

export type AgentRunLastEvents = {
  shot_prompt_updated?: AgentShotPromptUpdated | AgentShotPromptUpdated[] | null
  shot_duration_updated?: AgentShotDurationUpdated | null
  shot_video_references_updated?: AgentShotVideoReferencesUpdated | null
  shot_created?: AgentShotCreated | null
  shots_batch_queued?: AgentShotsBatchQueued | null
  shot_characters_updated?: AgentShotCharactersUpdated | null
  shot_location_updated?: AgentShotLocationUpdated | null
  shot_scene_updated?: AgentShotSceneUpdated | null
  entity_updated?: AgentEntityUpdated | null
  canvas_draft_updated?: AgentCanvasDraftUpdated | null
  scene3d_layout_updated?: Scene3dLayoutUpdated | null
  canvas_generation_requested?: { start_generation?: boolean } | null
  flow_node_prompt_updated?: AgentFlowNodePromptUpdated | null
  flow_changed?: { flow_id?: number } | null
  scene_created?: AgentSceneCreated | null
  notes_updated?: boolean | null
}

export type AgentRunRequestedBy = {
  id?: number
  email?: string
}

export type AgentRunState = {
  status: "idle" | "processing" | "cancel_requested" | "failed" | string
  phase?: string | null
  tool_name?: string | null
  started_at?: string | null
  stream_buffer?: string | null
  error?: string | null
  error_code?: string | null
  /** Monotonic id of the claimed run. 0 means this conversation has not been claimed yet. */
  generation?: number | null
  last_events?: AgentRunLastEvents | null
  requested_by?: AgentRunRequestedBy | null
}

export type AgentSessionSkill = {
  id: number
  name: string
  slug: string
  description?: string
}

export type AgentHistoryResponse = {
  ok: boolean
  conversation_id: number
  mode: string
  context_type: string
  context_id: number
  messages: AgentMessageRow[]
  /** Есть реплики старше первой в этом ответе. */
  has_older?: boolean
  pending_action: AgentPendingAction | null
  last_message_id?: number | null
  run?: AgentRunState | null
  available_skills?: AgentSessionSkill[]
  error?: string
  error_code?: string
}

export type AgentRunStateResponse = {
  ok: boolean
  conversation_id: number
  last_message_id?: number | null
  run?: AgentRunState | null
  error?: string
  error_code?: string
}

export type AgentRunsResponse = {
  ok: boolean
  runs: AgentProjectRun[]
  error?: string
}

export type AgentConfirmActionResult = {
  ok: boolean
  generation_job_id?: number | null
  shot_id?: number | null
  result?: (Record<string, unknown> & { start_generation?: boolean }) | null
  scene3d_layout_updated?: Scene3dLayoutUpdated | null
  error?: string
}

export type AgentChatResponse = {
  ok: boolean
  conversation_id?: number
  run?: AgentRunState | null
  last_message_id?: number | null
  user_message?: AgentMessageRow | null
  error?: string
  error_code?: string
}

function agentQuery(params: {
  mode?: string
  contextType: AgentContextType
  contextId: number
  selectedShotId?: number | null
  selectedEntityId?: number | null
  agentScope?: AgentApiScope
}): string {
  const q = new URLSearchParams()
  q.set("mode", params.mode || "shots_assistant")
  q.set("context_type", params.contextType)
  q.set("context_id", String(params.contextId))
  if (params.selectedShotId) q.set("selected_shot_id", String(params.selectedShotId))
  if (params.selectedEntityId) q.set("selected_entity_id", String(params.selectedEntityId))
  const companyId = agentScopeCompanyId(params.agentScope)
  if (companyId != null) q.set("company_id", String(companyId))
  return q.toString()
}

export async function fetchAgentHistory(params: {
  projectId?: number
  agentScope?: AgentApiScope
  mode?: string
  contextType: AgentContextType
  contextId: number
  beforeId?: number
  signal?: AbortSignal
}): Promise<AgentHistoryResponse> {
  const base = agentApiBase(agentScopeFromParams(params))
  const qs = new URLSearchParams(agentQuery(params))
  if (params.beforeId != null && params.beforeId > 0) qs.set("before_id", String(params.beforeId))
  const res = await fetch(`${base}/history?${qs}`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    signal: params.signal,
  })
  const data = (await res.json().catch(() => ({}))) as AgentHistoryResponse
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || "Failed to load agent history")
  }
  return data
}

export async function fetchAgentRunState(params: {
  projectId?: number
  agentScope?: AgentApiScope
  mode?: string
  contextType: AgentContextType
  contextId: number
  signal?: AbortSignal
}): Promise<AgentRunStateResponse> {
  const base = agentApiBase(agentScopeFromParams(params))
  const qs = agentQuery(params)
  const res = await fetch(`${base}/run-state?${qs}`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
    signal: params.signal,
  })
  const data = (await res.json().catch(() => ({}))) as AgentRunStateResponse
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || "Failed to load agent run state")
  }
  return data
}

/** Активные прогоны всех ассистентов проекта — гидрация плашек и fallback без cable. */
export async function fetchAgentRuns(projectIdOrScope: number | AgentApiScope): Promise<AgentProjectRun[]> {
  const scope = resolveAgentApiScope(projectIdOrScope)
  const companyId = agentScopeCompanyId(scope)
  const qs = companyId == null ? "" : `?company_id=${companyId}`
  const res = await fetch(`${agentApiBase(scope)}/runs${qs}`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
    },
  })
  const data = (await res.json().catch(() => ({}))) as AgentRunsResponse
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || "Failed to load agent runs")
  }
  return Array.isArray(data.runs) ? data.runs : []
}

type AgentChatField = {
  name: string
  /** Значение в JSON. Нет ключа — поле не отправляется. */
  json?: unknown
  /** Значение в multipart. Нет строки — поле не отправляется. */
  form?: string
}

/** Одни и те же поля чата для JSON и для FormData с картинками. */
function agentChatFields(params: {
  agentScope?: AgentApiScope
  projectId?: number
  message: string
  mode?: string
  contextType: AgentContextType
  contextId: number
  selectedShotId?: number | null
  selectedEntityId?: number | null
  episodeId?: number | null
  canvasDraft?: Record<string, unknown> | null
  scriptFocus?: { excerpt: string; block_ids: string[] } | null
  selectedTaskId?: number | null
  scene3dFocus?: Scene3dAgentFocus | null
}): AgentChatField[] {
  const mode = params.mode || "shots_assistant"
  const fields: AgentChatField[] = [
    { name: "message", json: params.message, form: params.message },
    { name: "mode", json: mode, form: mode },
    { name: "context_type", json: params.contextType, form: params.contextType },
    { name: "context_id", json: params.contextId, form: String(params.contextId) },
  ]
  const optional: Array<[string, number | null | undefined]> = [
    ["selected_shot_id", params.selectedShotId],
    ["selected_entity_id", params.selectedEntityId],
    ["episode_id", params.episodeId],
    ["selected_task_id", params.selectedTaskId],
  ]
  for (const [name, value] of optional) {
    if (value == null) continue
    fields.push({ name, json: value, form: String(value) })
  }
  if (params.canvasDraft) {
    fields.push({ name: "canvas_draft", json: params.canvasDraft, form: JSON.stringify(params.canvasDraft) })
  }
  if (params.scriptFocus) {
    fields.push({ name: "script_focus", json: params.scriptFocus, form: JSON.stringify(params.scriptFocus) })
  }
  if (params.scene3dFocus) {
    fields.push({ name: "scene3d_focus", json: params.scene3dFocus, form: JSON.stringify(params.scene3dFocus) })
  }
  const companyId = agentCompanyBody(agentScopeFromParams(params)).company_id
  if (companyId != null) fields.push({ name: "company_id", json: companyId, form: String(companyId) })
  return fields
}

function agentChatRequestBody(
  params: Parameters<typeof agentChatFields>[0] & {
    images?: File[]
    sources?: { entity_type: string; entity_id: number; state_id?: string }[]
    imageOrder?: Array<"upload" | "library">
  },
): { headers: Record<string, string>; body: BodyInit } {
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
    "X-CSRF-Token": getCsrfToken(),
  }
  const fields = agentChatFields(params)
  const hasFiles = (params.images?.length ?? 0) > 0 || (params.sources?.length ?? 0) > 0
  if (!hasFiles) {
    headers["Content-Type"] = "application/json"
    const json: Record<string, unknown> = {}
    for (const field of fields) {
      if (field.json !== undefined) json[field.name] = field.json
    }
    return { headers, body: JSON.stringify(json) }
  }

  const form = new FormData()
  for (const field of fields) {
    if (field.form != null) form.append(field.name, field.form)
  }
  for (const file of params.images ?? []) form.append("images[]", file)
  if ((params.sources?.length ?? 0) > 0) form.append("sources", JSON.stringify(params.sources))
  if ((params.imageOrder?.length ?? 0) > 0) form.append("image_order", JSON.stringify(params.imageOrder))
  return { headers, body: form }
}

export async function postAgentChat(params: {
  projectId?: number
  agentScope?: AgentApiScope
  message: string
  mode?: string
  contextType: AgentContextType
  contextId: number
  selectedShotId?: number | null
  selectedEntityId?: number | null
  episodeId?: number | null
  canvasDraft?: Record<string, unknown> | null
  scriptFocus?: { excerpt: string; block_ids: string[] } | null
  selectedTaskId?: number | null
  scene3dFocus?: Scene3dAgentFocus | null
  images?: File[]
  sources?: { entity_type: string; entity_id: number; state_id?: string }[]
  /** Composer order of uploads and library picks, so `@img_N` matches attachment order. */
  imageOrder?: Array<"upload" | "library">
}): Promise<AgentChatResponse> {
  const base = agentApiBase(agentScopeFromParams(params))
  const { headers, body } = agentChatRequestBody(params)
  const res = await fetch(`${base}/chat`, {
    method: "POST",
    credentials: "same-origin",
    headers,
    body,
  })
  const data = (await res.json().catch(() => ({ ok: false }))) as AgentChatResponse
  if (!res.ok || data.ok === false) {
    const err = new Error(data.error || data.error_code || `Agent chat failed (${res.status})`) as Error & {
      errorCode?: string
    }
    err.errorCode = data.error_code
    throw err
  }
  return data
}

export async function postAgentEditAndResend(params: {
  projectId?: number
  agentScope?: AgentApiScope
  messageId: number
  message: string
  mode?: string
  contextType: AgentContextType
  contextId: number
  selectedShotId?: number | null
  selectedEntityId?: number | null
  episodeId?: number | null
  canvasDraft?: Record<string, unknown> | null
  scriptFocus?: { excerpt: string; block_ids: string[] } | null
  selectedTaskId?: number | null
  scene3dFocus?: Scene3dAgentFocus | null
}): Promise<AgentChatResponse> {
  const base = agentApiBase(agentScopeFromParams(params))
  const res = await fetch(`${base}/edit-and-resend`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      message_id: params.messageId,
      message: params.message,
      mode: params.mode || "shots_assistant",
      context_type: params.contextType,
      context_id: params.contextId,
      selected_shot_id: params.selectedShotId ?? undefined,
      selected_entity_id: params.selectedEntityId ?? undefined,
      episode_id: params.episodeId ?? undefined,
      canvas_draft: params.canvasDraft ?? undefined,
      script_focus: params.scriptFocus ?? undefined,
      selected_task_id: params.selectedTaskId ?? undefined,
      scene3d_focus: params.scene3dFocus ?? undefined,
      ...agentCompanyBody(agentScopeFromParams(params)),
    }),
  })
  const data = (await res.json().catch(() => ({ ok: false }))) as AgentChatResponse
  if (!res.ok || data.ok === false) {
    const err = new Error(data.error || data.error_code || `Agent edit failed (${res.status})`) as Error & {
      errorCode?: string
    }
    err.errorCode = data.error_code
    throw err
  }
  return data
}

export async function cancelAgentRun(params: {
  projectId?: number
  agentScope?: AgentApiScope
  mode?: string
  contextType: AgentContextType
  contextId: number
}): Promise<void> {
  const base = agentApiBase(agentScopeFromParams(params))
  const res = await fetch(`${base}/cancel-run`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      mode: params.mode || "shots_assistant",
      context_type: params.contextType,
      context_id: params.contextId,
      ...agentCompanyBody(agentScopeFromParams(params)),
    }),
  })
  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string }
    throw new Error(data.error || "Failed to cancel agent run")
  }
}

export async function confirmAgentAction(params: {
  projectId?: number
  agentScope?: AgentApiScope
  mode?: string
  contextType: AgentContextType
  contextId: number
  episodeId?: number | null
  selectedShotId?: number | null
  selectedEntityId?: number | null
  canvasDraft?: Record<string, unknown> | null
  scene3dFocus?: Scene3dAgentFocus | null
  pendingAction?: AgentPendingAction | null
}): Promise<AgentConfirmActionResult> {
  const base = agentApiBase(agentScopeFromParams(params))
  const res = await fetch(`${base}/confirm-action`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      mode: params.mode || "shots_assistant",
      context_type: params.contextType,
      context_id: params.contextId,
      episode_id: params.episodeId ?? undefined,
      selected_shot_id: params.selectedShotId ?? undefined,
      selected_entity_id: params.selectedEntityId ?? undefined,
      canvas_draft: params.canvasDraft ?? undefined,
      scene3d_focus: params.scene3dFocus ?? undefined,
      pending_action: params.pendingAction ?? undefined,
      ...agentCompanyBody(agentScopeFromParams(params)),
    }),
  })
  return (await res.json().catch(() => ({ ok: false }))) as AgentConfirmActionResult
}

export async function cancelAgentAction(params: {
  projectId?: number
  agentScope?: AgentApiScope
  mode?: string
  contextType: AgentContextType
  contextId: number
}): Promise<void> {
  const base = agentApiBase(agentScopeFromParams(params))
  await fetch(`${base}/cancel-action`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: JSON.stringify({
      mode: params.mode || "shots_assistant",
      context_type: params.contextType,
      context_id: params.contextId,
      ...agentCompanyBody(agentScopeFromParams(params)),
    }),
  })
}

export type AgentTranscribeResponse = {
  ok: boolean
  text?: string
  duration_sec?: number
  error?: string
  error_code?: string
}

/**
 * Голосовой ввод: аудио → текст. Отдельно от postAgentChat — распознавание не
 * создаёт диалог и не занимает прогон агента.
 */
export async function postAgentTranscribe(
  projectIdOrScope: number | AgentApiScope,
  file: File,
  options: { signal?: AbortSignal } = {},
): Promise<string> {
  const scope = resolveAgentApiScope(projectIdOrScope)
  const form = new FormData()
  form.append("audio", file, file.name)
  const companyId = agentScopeCompanyId(scope)
  if (companyId != null) form.append("company_id", String(companyId))

  // Content-Type руками не ставим: boundary проставит браузер.
  const res = await fetch(`${agentApiBase(scope)}/transcribe`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    body: form,
    signal: options.signal,
  })

  const data = (await res.json().catch(() => ({ ok: false }) as AgentTranscribeResponse)) as AgentTranscribeResponse
  if (!res.ok || data.ok === false) {
    throw new VoiceTranscribeError(data.error || `Transcribe failed (${res.status})`, data.error_code)
  }
  return (data.text || "").trim()
}

/**
 * Прогрев STT при старте записи: спящий self-hosted Whisper поднимается, пока пользователь
 * говорит. Fire-and-forget — ответ не нужен, распознавание всё равно дождётся модели само.
 */
export function postAgentTranscribeWarmup(projectIdOrScope: number | AgentApiScope): void {
  const scope = resolveAgentApiScope(projectIdOrScope)
  const companyId = agentScopeCompanyId(scope)
  const qs = companyId == null ? "" : `?company_id=${companyId}`
  void fetch(`${agentApiBase(scope)}/transcribe/warmup${qs}`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "X-Requested-With": "XMLHttpRequest",
      "X-CSRF-Token": getCsrfToken(),
    },
    keepalive: true,
  }).catch(() => undefined)
}
